import { queryOptions, useQuery } from "@tanstack/react-query";
import { isRedirect, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import { authMiddleware } from "~/lib/auth-middleware";
import { hasPermission, type Permission } from "~/lib/shared/permissions";

/** The signed-in person's admin permissions; empty for most people. */
export const getMyPermissionsFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<Permission[]> => {
    const { getPermissionsForClerkUser } =
      await import("~/lib/server/permissions");
    return getPermissionsForClerkUser(context.user.clerkUserId);
  });

const myPermissionsQueryOptions = queryOptions({
  queryKey: ["my-permissions"],
  queryFn: () => getMyPermissionsFn(),
  staleTime: 5 * 60_000,
});

/** The signed-in person's admin permissions; empty while loading. */
export function useMyPermissions(): Permission[] {
  return useQuery(myPermissionsQueryOptions).data ?? [];
}

/**
 * Guards the `/admin` layout and returns the person's permissions for its
 * pages to check. Redirects to /login when signed out and to / without any
 * admin permission.
 *
 * Aliases: admin guard, admin route protection, require admin
 */
export async function requireAdminAccess(location: {
  pathname: string;
}): Promise<Permission[]> {
  const { fetchClerkAuth } = await import("~/routes/__root");
  const toLogin = () =>
    redirect({
      to: "/login",
      search: { from: "auth-failure", redirect: location.pathname } as const,
    });

  let permissions: Permission[];
  try {
    const { userId } = await fetchClerkAuth();
    if (!userId) throw toLogin();
    permissions = await getMyPermissionsFn();
  } catch (error) {
    // Let redirects pass through — including the handshake redirect that
    // `fetchClerkAuth` throws (Clerk's own nonce guards against loops).
    if (isRedirect(error)) throw error;
    throw toLogin();
  }

  if (permissions.length === 0) throw redirect({ to: "/" });
  return permissions;
}

/**
 * Guards one `/admin` page: redirects to the admin dashboard when the
 * permissions the `/admin` layout loaded lack `permission`.
 *
 * Aliases: require permission, admin page guard
 */
export function requirePermission(
  permissions: readonly Permission[],
  permission: Permission,
): void {
  if (!hasPermission(permissions, permission)) {
    throw redirect({ to: "/admin" });
  }
}
